import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Star, CheckCircle2, ShieldCheck } from "lucide-react";

export default function ReferencePage() {
  const { token } = useParams();
  const [state, setState] = useState(undefined); // undefined=loading, object, null=invalid
  const [rating, setRating] = useState(0);
  const [relationship, setRelationship] = useState("");
  const [testimonial, setTestimonial] = useState("");
  const [wouldWork, setWouldWork] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/reference/${token}`)
      .then((r) => { setState(r.data); setRelationship(r.data.relationship || ""); })
      .catch(() => setState(null));
  }, [token]);

  const submit = async () => {
    if (!rating) return toast.error("Please pick an overall rating");
    if (!testimonial.trim()) return toast.error("Please add a short testimonial");
    if (wouldWork === null) return toast.error("Let us know if you'd work with them again");
    setSubmitting(true);
    try {
      await api.post(`/public/reference/${token}`, {
        rating, testimonial, would_work_again: wouldWork, relationship,
      });
      setDone(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (state === undefined) return <div className="min-h-screen grid place-items-center text-muted font-mono text-sm">Loading…</div>;
  if (state === null) return (
    <div className="min-h-screen grid place-items-center p-6 text-center">
      <div>
        <ShieldCheck className="mx-auto text-muted mb-4" size={36} />
        <p className="font-display font-bold text-xl">Link unavailable</p>
        <p className="text-muted text-sm mt-1">This reference link is invalid or has expired.</p>
        <Link to="/" className="text-azure text-sm mt-4 inline-block">Go to Team Assembly →</Link>
      </div>
    </div>
  );

  const name = state.candidate_name;

  if (done || state.already_submitted) return (
    <div className="min-h-screen grid place-items-center p-6 text-center" data-testid="reference-done">
      <div>
        <CheckCircle2 className="mx-auto text-emerald mb-4" size={40} />
        <p className="font-display font-bold text-2xl">Thank you</p>
        <p className="text-muted text-sm mt-2 max-w-sm">Your reference for <span className="text-ink">{name}</span> has been recorded. It helps us verify their work with real evidence.</p>
        <Link to="/" className="text-azure text-sm mt-5 inline-block">Learn about Team Assembly →</Link>
      </div>
    </div>
  );

  const input = "w-full rounded-lg border border-white/12 bg-surface px-3.5 py-2.5 text-sm text-ink focus:border-azure focus:outline-none focus:ring-2 focus:ring-azure/40 transition-colors duration-200";
  const label = "label-mono text-faint block mb-1.5";

  return (
    <div className="min-h-screen ta-noise">
      <header className="ta-glass border-b border-white/10">
        <div className="mx-auto max-w-2xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <span className="label-mono text-faint">Reference request</span>
        </div>
      </header>
      <div className="mx-auto max-w-2xl px-5 lg:px-8 py-10 relative z-10">
        <p className="label-mono text-azure mb-1">Confidential reference</p>
        <h1 className="font-display font-bold text-3xl mb-2">Tell us about {name}</h1>
        <p className="text-sm text-muted mb-8">{state.referee_name ? `Hi ${state.referee_name}. ` : ""}{name} listed you as a professional reference. This takes about two minutes and stays confidential to our vetting team and verified employers.</p>

        <div className="rounded-2xl border border-white/10 bg-surface p-7 space-y-6">
          <div>
            <label className={label}>Your relationship to {name}</label>
            <input className={input} value={relationship} onChange={(e) => setRelationship(e.target.value)}
              data-testid="ref-relationship" placeholder="e.g. Former manager at Acme" />
          </div>

          <div>
            <label className={label}>Overall rating</label>
            <div className="flex gap-1.5" data-testid="ref-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setRating(n)} data-testid={`ref-star-${n}`}
                  className="transition-transform duration-150 hover:scale-110">
                  <Star size={30} className={n <= rating ? "fill-amber text-amber" : "text-white/20"} />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={label}>Testimonial — what makes them exceptional?</label>
            <textarea rows={5} className={input} value={testimonial} onChange={(e) => setTestimonial(e.target.value)}
              data-testid="ref-testimonial" placeholder="Their impact, how they think, and where they perform best…" />
          </div>

          <div>
            <label className={label}>Would you work with {name} again?</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setWouldWork(true)} data-testid="ref-would-yes"
                className={`rounded-full px-5 py-2 text-sm font-medium transition-colors duration-200 ${wouldWork === true ? "bg-emerald text-black" : "border border-white/15 text-ink hover:bg-white/5"}`}>Yes, absolutely</button>
              <button type="button" onClick={() => setWouldWork(false)} data-testid="ref-would-no"
                className={`rounded-full px-5 py-2 text-sm font-medium transition-colors duration-200 ${wouldWork === false ? "bg-signal text-white" : "border border-white/15 text-ink hover:bg-white/5"}`}>No</button>
            </div>
          </div>

          <button onClick={submit} disabled={submitting} data-testid="ref-submit-btn"
            className="w-full rounded-full bg-azure px-6 py-3 text-sm font-medium text-white hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50">
            {submitting ? "Submitting…" : "Submit reference"}
          </button>
          <p className="text-xs text-faint text-center">Team Assembly · Evidence-based technical hiring · Your response is confidential.</p>
        </div>
      </div>
    </div>
  );
}
